import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HomePage } from './components/HomePage';
import { ShutdownImpactMap } from './components/ShutdownImpactMap';

const ROUTE_META: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Davis Community Tools - Davis, CA',
    description:
      'Open tools and resources for the Davis, CA community. Explore the DJUSD School Shutdown Impact Map to visualize elementary school closure impacts on enrollment and student travel distances.',
  },
  '/djusd-shutdown-impact-map': {
    title: 'DJUSD School Shutdown Impact Map - Davis Community Tools',
    description:
      'Interactive map to visualize the impact of DJUSD elementary school closures. Toggle schools on/off to see enrollment redistribution and distance impacts across 246 study areas.',
  },
};

function usePageMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = ROUTE_META[pathname] ?? ROUTE_META['/'];
    document.title = meta.title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', meta.description);
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute('content', meta.title);
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute('content', meta.description);
    document
      .querySelector('meta[name="twitter:title"]')
      ?.setAttribute('content', meta.title);
    document
      .querySelector('meta[name="twitter:description"]')
      ?.setAttribute('content', meta.description);
  }, [pathname]);
}

function Navigation() {
  const { pathname } = useLocation();
  const isMap = pathname === '/djusd-shutdown-impact-map';

  // Hide nav on full-screen map view
  if (isMap) return null;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-gray-200">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-davis-navy to-davis-navy-light flex items-center justify-center shadow group-hover:shadow-md transition-shadow">
              <svg
                viewBox="0 0 24 32"
                className="w-5 h-6 text-davis-gold"
                fill="currentColor"
              >
                <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" />
                <circle cx="12" cy="11" r="5" fill="white" opacity="0.9" />
              </svg>
            </div>
            <div className="text-left">
              <h1 className="text-base font-display text-davis-navy leading-tight">
                Davis Community Tools
              </h1>
              <p className="text-[10px] text-davis-slate">
                Davis, CA
              </p>
            </div>
          </Link>
          <nav className="flex items-center gap-4">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                pathname === '/'
                  ? 'text-davis-navy'
                  : 'text-davis-slate hover:text-davis-navy'
              }`}
            >
              Home
            </Link>
            <Link
              to="/djusd-shutdown-impact-map"
              className={`text-sm font-medium transition-colors ${
                pathname === '/djusd-shutdown-impact-map'
                  ? 'text-davis-navy'
                  : 'text-davis-slate hover:text-davis-navy'
              }`}
            >
              Impact Map
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { pathname } = useLocation();

  // Hide footer on full-screen map view
  if (pathname === '/djusd-shutdown-impact-map') return null;

  return (
    <footer className="bg-davis-navy text-white py-6 mt-auto">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <a
            href="https://baycrewtech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-white/90 font-medium hover:text-davis-gold transition-colors flex items-center gap-1.5"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-davis-gold" fill="currentColor">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M12 2 L14 10 L12 8 L10 10 Z" />
              <path d="M12 22 L10 14 L12 16 L14 14 Z" />
              <path d="M2 12 L10 10 L8 12 L10 14 Z" />
              <path d="M22 12 L14 14 L16 12 L14 10 Z" />
            </svg>
            &copy; {new Date().getFullYear()} Bay Crew Tech, LLC
          </a>
          <span className="text-sm text-white/90 font-medium">
            Open tools for the Davis, CA community
          </span>
        </div>
      </div>
    </footer>
  );
}

function Layout() {
  usePageMeta();

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1 flex flex-col">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/djusd-shutdown-impact-map"
            element={<ShutdownImpactMap />}
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Layout />
    </BrowserRouter>
  );
}

export default App;
